import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { OrganizationPicker } from '../common';
import {
  FileText, Users, LogIn, Search, Calendar, Filter,
  AlertCircle, Loader2, ChevronDown, ChevronUp, Download,
  Settings, CheckCircle, XCircle, Cloud, RefreshCw, Trash2, Save, TestTube, Lock, Eye, EyeOff
} from 'lucide-react';
import { useParadigm } from '../../contexts/ParadigmContext';

const LOG_TABS = [
  { id: 'trip', label: 'Trip Audit', icon: FileText, description: 'Changes to trips, components, and collaborators' },
  { id: 'user', label: 'User Creation', icon: Users, description: 'New user registrations and invites' },
  { id: 'login', label: 'Login History', icon: LogIn, description: 'User login events with IP addresses' },
  { id: 'confidential', label: 'Confidential Access', icon: Lock, description: 'Confidential document reveal/conceal events' },
];

const CHANGE_TYPES = {
  created: { label: 'Created', color: 'green' },
  updated: { label: 'Updated', color: 'blue' },
  deleted: { label: 'Deleted', color: 'red' },
  archived: { label: 'Archived', color: 'gray' },
  unarchived: { label: 'Unarchived', color: 'green' },
  component_added: { label: 'Component Added', color: 'green' },
  component_updated: { label: 'Component Updated', color: 'blue' },
  component_removed: { label: 'Component Removed', color: 'red' },
  collaborator_added: { label: 'Collaborator Added', color: 'purple' },
  collaborator_removed: { label: 'Collaborator Removed', color: 'orange' },
  status_changed: { label: 'Status Changed', color: 'yellow' },
  executive_changed: { label: 'Executive Changed', color: 'indigo' },
};

const LOGIN_METHODS = {
  password: { label: 'Password', color: 'blue' },
  google_sso: { label: 'Google SSO', color: 'red' },
  microsoft_sso: { label: 'Microsoft SSO', color: 'blue' },
  passkey: { label: 'Passkey', color: 'green' },
};

const CREATION_METHODS = {
  invite: { label: 'Invite', color: 'blue' },
  self_onboard: { label: 'Self Onboard', color: 'green' },
  admin_created: { label: 'Admin Created', color: 'purple' },
  sso: { label: 'SSO', color: 'orange' },
};

const AuditLogs = () => {
  const { getRoleLabel } = useParadigm();
  const [activeTab, setActiveTab] = useState('trip');
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState('');

  // Superadmin organization selector
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgsLoading, setOrgsLoading] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // S3 Export Config
  const [showExportConfig, setShowExportConfig] = useState(false);
  const [exportConfig, setExportConfig] = useState(null);
  const [exportConfigLoading, setExportConfigLoading] = useState(false);
  const [exportConfigForm, setExportConfigForm] = useState({
    s3_bucket_name: '',
    s3_region: 'us-east-1',
    s3_access_key_id: '',
    s3_secret_access_key: '',
    s3_prefix: 'audit-logs/',
    export_frequency: 'daily',
    export_format: 'json',
    export_trip_audit: true,
    export_user_creation: true,
    export_login: true,
    retention_days: 90,
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { user } = useAuth();
  const limit = 50;
  const isSuperadmin = user?.role === 'superadmin';
  const isOrgAdmin = user?.is_organization_admin;
  const [rbacStatus, setRbacStatus] = useState(null);

  // Helper to check RBAC permission
  const hasRbacPermission = (permission) => {
    if (!rbacStatus?.rbac_enabled) return false;
    const userPerms = rbacStatus?.user_permissions || [];
    if (userPerms.includes('org_admin')) return true;
    return userPerms.includes(permission);
  };

  // Check if user can configure S3 export
  const canConfigureExport = isSuperadmin || isOrgAdmin || hasRbacPermission('audit_logs_configure');

  // Load organizations for superadmin
  const loadOrganizations = useCallback(async () => {
    if (!isSuperadmin) return;

    setOrgsLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/me/accessible-organizations`, { headers });
      setOrganizations(res.data || []);
      // Select first org by default if none selected
      if (res.data?.length > 0 && !selectedOrgId) {
        setSelectedOrgId(res.data[0].id);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    } finally {
      setOrgsLoading(false);
    }
  }, [isSuperadmin, selectedOrgId]);

  const checkFeatureStatus = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (isSuperadmin && selectedOrgId) {
        params.append('organization_id', selectedOrgId);
      }
      const res = await axios.get(`${apiBase}/audit-logs/feature-status?${params}`, { headers });
      setFeatureEnabled(res.data.enabled);
    } catch (err) {
      console.error('Failed to check feature status:', err);
      setFeatureEnabled(false);
    }
  }, [isSuperadmin, selectedOrgId]);

  const loadLogs = useCallback(async () => {
    if (!featureEnabled) return;
    // For superadmin, require an org to be selected
    if (isSuperadmin && !selectedOrgId) return;

    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      let endpoint;
      switch (activeTab) {
        case 'trip':
          endpoint = 'trip';
          break;
        case 'user':
          endpoint = 'user-creation';
          break;
        case 'login':
          endpoint = 'login';
          break;
        case 'confidential':
          endpoint = 'confidential-access';
          break;
        default:
          endpoint = 'trip';
      }

      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());
      if (isSuperadmin && selectedOrgId) {
        params.append('organization_id', selectedOrgId);
      }

      const res = await axios.get(`${apiBase}/audit-logs/${endpoint}?${params}`, { headers });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setError('');
    } catch (err) {
      console.error('Failed to load logs:', err);
      setError(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, startDate, endDate, featureEnabled, isSuperadmin, selectedOrgId]);

  const loadExportConfig = useCallback(async () => {
    if (!featureEnabled) return;

    setExportConfigLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/audit-logs/export-config`, { headers });
      setExportConfig(res.data.config);
      if (res.data.config) {
        setExportConfigForm(prev => ({
          ...prev,
          s3_bucket_name: res.data.config.s3_bucket_name || '',
          s3_region: res.data.config.s3_region || 'us-east-1',
          s3_prefix: res.data.config.s3_prefix || 'audit-logs/',
          export_frequency: res.data.config.export_frequency || 'daily',
          export_format: res.data.config.export_format || 'json',
          export_trip_audit: res.data.config.export_trip_audit ?? true,
          export_user_creation: res.data.config.export_user_creation ?? true,
          export_login: res.data.config.export_login ?? true,
          retention_days: res.data.config.retention_days || 90,
        }));
      }
    } catch (err) {
      console.error('Failed to load export config:', err);
    } finally {
      setExportConfigLoading(false);
    }
  }, [featureEnabled]);

  // Fetch RBAC status on mount
  useEffect(() => {
    const fetchRbacStatus = async () => {
      try {
        const res = await axios.get(`${getApiBase()}/rbac/status`, { headers: getAuthHeaders() });
        setRbacStatus(res.data);
      } catch (err) {
        // RBAC may not be enabled
        console.log('RBAC status not available');
      }
    };
    fetchRbacStatus();
  }, []);

  // Load organizations for superadmin on mount
  useEffect(() => {
    if (isSuperadmin) {
      loadOrganizations();
    }
  }, [isSuperadmin, loadOrganizations]);

  useEffect(() => {
    // For superadmins, wait until an org is selected
    if (isSuperadmin && !selectedOrgId) return;
    checkFeatureStatus();
  }, [checkFeatureStatus, isSuperadmin, selectedOrgId]);

  useEffect(() => {
    if (featureEnabled) {
      loadLogs();
      loadExportConfig();
    }
  }, [featureEnabled, loadLogs, loadExportConfig]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, startDate, endDate, selectedOrgId]);

  const handleSaveExportConfig = async () => {
    setExportConfigLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      if (exportConfig) {
        await axios.put(`${apiBase}/audit-logs/export-config`, exportConfigForm, { headers });
      } else {
        await axios.post(`${apiBase}/audit-logs/export-config`, exportConfigForm, { headers });
      }

      await loadExportConfig();
      setError('');
    } catch (err) {
      console.error('Failed to save export config:', err);
      setError(err.response?.data?.detail || 'Failed to save export configuration');
    } finally {
      setExportConfigLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.post(`${apiBase}/audit-logs/export-config/test`, {}, { headers });

      if (res.data.success) {
        alert('Connection successful! S3 bucket is accessible.');
      } else {
        alert(`Connection failed: ${res.data.error}`);
      }
    } catch (err) {
      console.error('Failed to test connection:', err);
      alert('Failed to test connection');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleExportNow = async () => {
    setExporting(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.post(`${apiBase}/audit-logs/export`, {}, { headers });

      if (res.data.success) {
        alert(`Export successful! ${res.data.exported_files?.length || 0} files exported.`);
      } else {
        alert(`Export failed: ${res.data.error}`);
      }
    } catch (err) {
      console.error('Failed to export:', err);
      alert('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!window.confirm('Are you sure you want to delete the S3 export configuration?')) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/audit-logs/export-config`, { headers });
      setExportConfig(null);
      setExportConfigForm({
        s3_bucket_name: '',
        s3_region: 'us-east-1',
        s3_access_key_id: '',
        s3_secret_access_key: '',
        s3_prefix: 'audit-logs/',
        export_frequency: 'daily',
        export_format: 'json',
        export_trip_audit: true,
        export_user_creation: true,
        export_login: true,
        retention_days: 90,
      });
    } catch (err) {
      console.error('Failed to delete config:', err);
      alert('Failed to delete configuration');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const renderTripLogs = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trip</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changed By</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {logs.map((log) => {
          const changeType = CHANGE_TYPES[log.change_type] || { label: log.change_type, color: 'gray' };
          return (
            <tr key={log.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.timestamp)}</td>
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.trip_title}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${changeType.color}-100 text-${changeType.color}-800`}>
                  {changeType.label}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{log.changed_by_name || log.changed_by_email}</td>
              <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={log.change_description}>
                {log.change_description}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 font-mono">{log.ip_address || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderUserLogs = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {logs.map((log) => {
          const method = CREATION_METHODS[log.creation_method] || { label: log.creation_method, color: 'gray' };
          return (
            <tr key={log.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.timestamp)}</td>
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.user_name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{log.user_email}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{getRoleLabel(log.user_role)}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${method.color}-100 text-${method.color}-800`}>
                  {method.label}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{log.created_by_name || log.created_by_email || '-'}</td>
              <td className="px-4 py-3 text-sm text-gray-500 font-mono">{log.ip_address || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderLoginLogs = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {logs.map((log) => {
          const method = LOGIN_METHODS[log.login_method] || { label: log.login_method, color: 'gray' };
          return (
            <tr key={log.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.timestamp)}</td>
              <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.user_name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{log.user_email}</td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${method.color}-100 text-${method.color}-800`}>
                  {method.label}
                </span>
              </td>
              <td className="px-4 py-3">
                {log.login_success ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" /> Success
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" /> Failed
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 font-mono">{log.ip_address || '-'}</td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {log.city && log.country ? `${log.city}, ${log.country}` : '-'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderConfidentialLogs = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accessed By</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Component Type</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trip</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {logs.map((log) => {
          const isReveal = log.action === 'reveal';
          const roleColors = {
            org_admin: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Org Admin' },
            supervisor: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Supervisor' },
            trip_creator: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Trip Creator' }
          };
          const roleStyle = roleColors[log.accessor_role] || { bg: 'bg-gray-100', text: 'text-gray-800', label: log.accessor_role };

          const componentTypeNames = {
            'commercial_flight': 'Flight',
            'private_flight': 'Private Flight',
            'hotel': 'Hotel',
            'rental_car': 'Car Rental',
            'car_rental': 'Car Rental',
            'restaurant': 'Restaurant',
            'meeting': 'Meeting',
            'event': 'Event',
            'chauffeur_service': 'Chauffeur Service',
            'memo': 'Memo'
          };

          return (
            <tr key={log.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.timestamp)}</td>
              <td className="px-4 py-3">
                {isReveal ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <Eye className="w-3 h-3 mr-1" /> Revealed
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                    <EyeOff className="w-3 h-3 mr-1" /> Concealed
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">{log.accessed_by_name}</div>
                <div className="text-xs text-gray-500">{log.accessed_by_email}</div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleStyle.bg} ${roleStyle.text}`}>
                  {roleStyle.label}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {componentTypeNames[log.component_type] || log.component_type}
                {log.is_unfiled && (
                  <span className="ml-1 text-xs text-gray-400">(Unfiled)</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-gray-900">{log.content_creator_name}</div>
                <div className="text-xs text-gray-500">{log.content_creator_email}</div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {log.trip_title || (log.is_unfiled ? 'Inbox' : '-')}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 font-mono">{log.ip_address || '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // Superadmin loading organizations
  if (isSuperadmin && orgsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading organizations...</span>
      </div>
    );
  }

  // Superadmin with no organizations
  if (isSuperadmin && !orgsLoading && organizations.length === 0) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">No Organizations Found</h2>
            <p className="text-yellow-700">
              There are no organizations in the system to view audit logs for.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Feature not enabled view (only for non-superadmins)
  if (featureEnabled === false && !isSuperadmin) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">Audit Logs Not Available</h2>
            <p className="text-yellow-700">
              Audit logs are not enabled for your organization's package.
              Contact your administrator to upgrade your package to access this feature.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (featureEnabled === null && !isSuperadmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-600 mt-1">
              {isSuperadmin ? 'View activity logs across organizations' : 'View activity logs for your organization'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Organization selector for superadmins */}
            {isSuperadmin && (
              <div className="w-72">
                <OrganizationPicker
                  organizations={organizations}
                  value={selectedOrgId}
                  onChange={(orgId) => setSelectedOrgId(orgId)}
                  placeholder="Select organization..."
                  loading={orgsLoading}
                  showDomain
                />
              </div>
            )}
            {canConfigureExport && (
              <button
                onClick={() => setShowExportConfig(!showExportConfig)}
                className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4 mr-2" />
                S3 Export Settings
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* S3 Export Configuration Panel */}
        {showExportConfig && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Cloud className="w-5 h-5 mr-2 text-blue-600" />
                S3 Export Configuration
              </h3>
              {exportConfig && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleTestConnection}
                    disabled={testingConnection}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                  >
                    {testingConnection ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <TestTube className="w-4 h-4 mr-1" />}
                    Test Connection
                  </button>
                  <button
                    onClick={handleExportNow}
                    disabled={exporting}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Export Now
                  </button>
                  <button
                    onClick={handleDeleteConfig}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">S3 Bucket Name</label>
                <input
                  type="text"
                  value={exportConfigForm.s3_bucket_name}
                  onChange={(e) => setExportConfigForm(prev => ({ ...prev, s3_bucket_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="my-audit-logs-bucket"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">S3 Region</label>
                <select
                  value={exportConfigForm.s3_region}
                  onChange={(e) => setExportConfigForm(prev => ({ ...prev, s3_region: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-east-2">US East (Ohio)</option>
                  <option value="us-west-1">US West (N. California)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">EU (Ireland)</option>
                  <option value="eu-central-1">EU (Frankfurt)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Key ID</label>
                <input
                  type="text"
                  value={exportConfigForm.s3_access_key_id}
                  onChange={(e) => setExportConfigForm(prev => ({ ...prev, s3_access_key_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={exportConfig ? '****' + (exportConfig.s3_access_key_id_masked || '') : 'AKIAIOSFODNN7EXAMPLE'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secret Access Key</label>
                <input
                  type="password"
                  value={exportConfigForm.s3_secret_access_key}
                  onChange={(e) => setExportConfigForm(prev => ({ ...prev, s3_secret_access_key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter secret key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">S3 Prefix (Folder)</label>
                <input
                  type="text"
                  value={exportConfigForm.s3_prefix}
                  onChange={(e) => setExportConfigForm(prev => ({ ...prev, s3_prefix: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="audit-logs/"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Export Format</label>
                <select
                  value={exportConfigForm.export_format}
                  onChange={(e) => setExportConfigForm(prev => ({ ...prev, export_format: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Log Types to Export</label>
              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfigForm.export_trip_audit}
                    onChange={(e) => setExportConfigForm(prev => ({ ...prev, export_trip_audit: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Trip Audit</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfigForm.export_user_creation}
                    onChange={(e) => setExportConfigForm(prev => ({ ...prev, export_user_creation: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">User Creation</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfigForm.export_login}
                    onChange={(e) => setExportConfigForm(prev => ({ ...prev, export_login: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Login History</span>
                </label>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSaveExportConfig}
                disabled={exportConfigLoading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                {exportConfigLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Configuration
              </button>
            </div>

            {exportConfig?.last_export_at && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Last export: {formatDate(exportConfig.last_export_at)} -
                  <span className={exportConfig.last_export_status === 'success' ? 'text-green-600' : 'text-red-600'}>
                    {' '}{exportConfig.last_export_status}
                  </span>
                </p>
                {exportConfig.last_export_error && (
                  <p className="text-sm text-red-600 mt-1">Error: {exportConfig.last_export_error}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          {LOG_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>

          {showFilters && (
            <div className="mt-3 p-4 bg-gray-50 rounded-lg flex items-center space-x-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="mt-5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <FileText className="w-12 h-12 mb-4 text-gray-300" />
              <p>No logs found for the selected criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {activeTab === 'trip' && renderTripLogs()}
              {activeTab === 'user' && renderUserLogs()}
              {activeTab === 'login' && renderLoginLogs()}
              {activeTab === 'confidential' && renderConfidentialLogs()}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} entries
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
